import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Zap, Shield, Fuel, Play, X } from 'lucide-react';
import { featuresData } from '../mockData';

const iconMap = {
  Zap: Zap,
  Shield: Shield,
  Fuel: Fuel
};

export const FeaturesSection = () => {
  const [playingVideo, setPlayingVideo] = useState(null);

  const featureVideos = {
    1: 'https://iframe.mediadelivery.net/embed/604767/c227f5d5-3c38-433d-8105-593f6095817a',
    2: 'https://iframe.mediadelivery.net/embed/604767/49b6841f-1d01-4403-97cf-b12792e82701',
    3: 'https://iframe.mediadelivery.net/embed/604767/8d501bd8-4526-4fe5-93a0-90ef244bc686'
  };

  const handleCardClick = (featureId) => {
    if (playingVideo === featureId) {
      setPlayingVideo(null);
    } else {
      setPlayingVideo(featureId);
    }
  };

  return (
    <section id="features" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="w-20 h-1 bg-red-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our <span className="text-red-600 font-semibold">Robotic Coupling System</span> addresses the most critical challenges in modern trucking operations
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {featuresData.map((feature) => {
            const IconComponent = iconMap[feature.icon];
            const isPlaying = playingVideo === feature.id;
            const hasVideo = featureVideos[feature.id];
            
            // Hide non-playing cards when one is playing
            if (playingVideo !== null && !isPlaying) {
              return null;
            }

            return (
              <Card 
                key={feature.id}
                className={`bg-white border-gray-200 transition-all duration-300 overflow-hidden ${
                  isPlaying 
                    ? 'md:col-span-3 border-red-600 shadow-2xl' 
                    : 'hover:border-red-600 hover:shadow-xl hover:transform hover:scale-105'
                } group relative`}
              >
                {isPlaying ? (
                  // Video Playing State - Content-based sizing
                  <div className="relative">
                    {/* Close Button */}
                    <button
                      onClick={() => setPlayingVideo(null)}
                      className="absolute top-4 right-4 z-50 w-12 h-12 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                    >
                      <X size={24} />
                    </button>

                    {hasVideo ? (
                      // Show actual video - using Bunny.net responsive embed
                      <div className="w-full">
                        <div className="bg-white px-6 pt-6 pb-4">
                          <h3 className="text-2xl font-bold text-gray-900">{feature.title}</h3>
                        </div>
                        
                        {/* Responsive video container */}
                        <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                          <iframe 
                            src={`${featureVideos[feature.id]}?autoplay=true&loop=false&muted=true&preload=true&responsive=true`}
                            loading="lazy"
                            style={{ border: 0, position: 'absolute', top: 0, height: '100%', width: '100%' }}
                            allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                            allowFullScreen={true}
                            title={feature.title}
                          />
                        </div>
                        
                        {/* Info below video */}
                        <div className="bg-white px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-gray-50 rounded-lg">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Impact</p>
                              <p className="text-lg font-semibold text-red-600">{feature.stats}</p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-lg">
                              <p className="text-gray-700">{feature.description}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Show coming soon message
                      <div className="w-full">
                        <div className="bg-white px-6 pt-6 pb-4">
                          <h3 className="text-2xl font-bold text-gray-900">{feature.title}</h3>
                        </div>
                        
                        <div style={{ position: 'relative', paddingTop: '56.25%' }} className="bg-gradient-to-br from-gray-50 to-gray-100 border-y-2 border-gray-200">
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
                              <Play className="text-red-600" size={40} />
                            </div>
                            <h4 className="text-2xl font-bold text-gray-900 mb-3">Demo Video Coming Soon</h4>
                            <p className="text-gray-600 text-center max-w-lg px-4">
                              We're preparing a detailed demonstration of this feature
                            </p>
                          </div>
                        </div>
                        
                        {/* Info below placeholder */}
                        <div className="bg-white px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-gray-50 rounded-lg">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Impact</p>
                              <p className="text-lg font-semibold text-red-600">{feature.stats}</p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-lg">
                              <p className="text-gray-700">{feature.description}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Default Card State - with video preview background
                  <div className="cursor-pointer" onClick={() => handleCardClick(feature.id)}>
                    {/* Video Preview */}
                    <div className="relative aspect-video overflow-hidden">
                      <iframe
                        src={`${featureVideos[feature.id]}?autoplay=true&loop=true&muted=true&preload=true&responsive=true`}
                        loading="lazy"
                        style={{ border: 0, position: 'absolute', top: 0, height: '100%', width: '100%', pointerEvents: 'none' }}
                        allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                        title={feature.title}
                      />
                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center" style={{ pointerEvents: 'none' }}>
                        <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <Play className="text-white ml-1" size={28} fill="white" />
                        </div>
                      </div>
                    </div>
                    
                    <CardHeader className="pb-2">
                      <CardTitle className="text-2xl text-gray-900 mb-2 group-hover:text-red-600 transition-colors duration-300">
                        {feature.title}
                      </CardTitle>
                      <CardDescription className="text-gray-600 text-base leading-relaxed">
                        {feature.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="pt-4 border-t border-gray-200">
                        <div className="text-sm text-gray-500 uppercase tracking-wider mb-1">Impact</div>
                        <div className="text-lg font-semibold text-red-600">{feature.stats}</div>
                      </div>
                    </CardContent>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gray-50 border border-gray-200 rounded-lg px-8 py-6">
            <p className="text-gray-700 text-lg">
              <span className="text-red-600 font-bold">Drop-in replacement</span> for standard fifth wheels
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
